import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Radio, Tv, PlayCircle, Users, Facebook, Instagram, Linkedin, Globe } from "lucide-react";

const XIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);
const TikTokIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>
);

export const SOCIAL_LINKS = [
  { key: "facebook", label: "B&B KIGALI on Facebook", href: "https://www.facebook.com/bbkigalifm", Icon: Facebook },
  { key: "x", label: "B&B KIGALI on X (Twitter)", href: "https://twitter.com/bbkigalifm", Icon: XIcon },
  { key: "instagram", label: "B&B KIGALI on Instagram", href: "https://www.instagram.com/bbkigalifm", Icon: Instagram },
  { key: "tiktok", label: "B&B KIGALI on TikTok", href: "https://www.tiktok.com/@bbfmkigali", Icon: TikTokIcon },
  { key: "linkedin", label: "B&B KIGALI on LinkedIn", href: "https://www.linkedin.com/in/bb-kigali-fm-5258461b2/", Icon: Linkedin },
  { key: "website", label: "B&B KIGALI FM website", href: "https://bbkigalifm.com/rw/", Icon: Globe },
];

export const SocialLinks = ({ prefix = "footer" }) => (
  <ul className="flex flex-wrap gap-3" aria-label="B&B KIGALI social media">
    {SOCIAL_LINKS.map(({ key, label, href, Icon }) => (
      <li key={key}>
        <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} data-testid={`${prefix}-social-${key}`}
          className="w-9 h-9 rounded-full bg-[#121620] border border-[#273042] flex items-center justify-center text-slate-300 hover:text-white hover:border-[#E11D2A]/60 transition-colors">
          <Icon className="w-4 h-4" />
        </a>
      </li>
    ))}
  </ul>
);

const L = ({ to, children, testid }) => <li><Link to={to} data-testid={testid} className="hover:text-white transition-colors">{children}</Link></li>;

const VisitorCount = () => {
  const [c, setC] = useState(null);
  useEffect(() => {
    const load = () => api.get("/analytics/visitors/public").then((r) => setC(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!c) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-300" data-testid="footer-visitor-count">
      <Users className="w-3.5 h-3.5 text-[#E11D2A]" />
      <b data-testid="visitors-today" className="text-white">{c.today.toLocaleString()}</b> visitors today ·
      <b data-testid="visitors-online" className="text-white">{c.online.toLocaleString()}</b> online now
    </span>
  );
};

export const Footer = () => (
  <footer className="border-t border-[#273042] bg-[#0A0C10] mt-16">
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
      <div className="col-span-2 md:col-span-1">
        <img src="/bb-logo.png" alt="B&B KIGALI FM 89.7" className="h-11 w-auto mb-4" />
        <p className="text-[#EF4444] font-extrabold text-sm tracking-wide uppercase mb-2">MURI SIPORO, NI IGITEGO!</p>
        <p className="text-sm text-slate-300 leading-relaxed">B&B KIGALI 89.7 — live radio, live TV and on-demand shows for Kigali and the diaspora worldwide.</p>
        <div className="mt-5"><SocialLinks /></div>
      </div>
      <nav aria-label="Watch and listen">
        <h4 className="text-white font-semibold text-sm mb-4">Watch & Listen</h4>
        <ul className="space-y-2.5 text-sm text-slate-300">
          <li><Link to="/radio" className="hover:text-white flex items-center gap-2"><Radio className="w-3.5 h-3.5" aria-hidden="true" /> Live Radio</Link></li>
          <li><Link to="/live" className="hover:text-white flex items-center gap-2"><Tv className="w-3.5 h-3.5" aria-hidden="true" /> Live TV</Link></li>
          <li><Link to="/vod" className="hover:text-white flex items-center gap-2"><PlayCircle className="w-3.5 h-3.5" aria-hidden="true" /> Shows / VOD</Link></li>
          <L to="/schedule">Schedule</L>
        </ul>
      </nav>
      <nav aria-label="Company">
        <h4 className="text-white font-semibold text-sm mb-4">Company</h4>
        <ul className="space-y-2.5 text-sm text-slate-300">
          <L to="/news">News</L>
          <L to="/programs">Programs</L>
          <L to="/pricing">Membership</L>
          <L to="/contact" testid="footer-contact-link">Contact</L>
        </ul>
      </nav>
      <nav aria-label="Legal">
        <h4 className="text-white font-semibold text-sm mb-4">Legal</h4>
        <ul className="space-y-2.5 text-sm text-slate-300">
          <L to="/terms" testid="footer-terms-link">Terms of Service</L>
          <L to="/privacy-policy" testid="footer-privacy-link">Privacy Policy</L>
          <L to="/refund-policy" testid="footer-refund-link">Refund Policy</L>
        </ul>
      </nav>
    </div>
    <div className="border-t border-[#273042] py-5 px-4 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 text-center text-xs text-slate-400">
      <span>© {new Date().getFullYear()} B&B KIGALI 89.7 · Broadcasting worldwide 🇷🇼</span>
      <VisitorCount />
      <div className="flex items-center gap-4">
        <button onClick={() => window.dispatchEvent(new Event("bbk-open-install"))} data-testid="footer-install-btn" className="hover:text-white transition-colors">Install app</button>
        <button onClick={() => window.dispatchEvent(new Event("open-staff-login"))} data-testid="footer-staff-login-btn" className="hover:text-white transition-colors">Staff login</button>
      </div>
    </div>
  </footer>
);
